import { useEffect } from 'react';
import Footer from '../../components/feature/Footer';

export default function CookiePolicyPage() {
  useEffect(() => {
    updatePageSEO({
      title: 'Cookie Policy | Curtis Waters AI Solutions',
      description: 'Cookie policy for Curtis Waters AI Solutions. Understand how we use cookies, tracking technologies, and analytics to improve your experience with our AI-powered deal evaluation and business intelligence platform.',
      keywords: 'cookie policy, cookies, tracking, analytics, web cookies, AI platform cookies, business intelligence cookies, website tracking, user preferences',
      canonical: `${import.meta.env.VITE_SITE_URL}/cookie-policy`,
      ogTitle: 'Cookie Policy | Curtis Waters AI Solutions',
      ogDescription: 'Cookie policy for Curtis Waters AI Solutions. Understand how we use cookies, tracking technologies, and analytics to improve your experience with our AI-powered deal evaluation and business intelligence platform.',
      ogUrl: `${import.meta.env.VITE_SITE_URL}/cookie-policy`,
    });
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://curtiswaters.ai';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Cookie Policy - Curtis Waters AI",
      "description": "Cookie Policy for Curtis Waters AI. Learn about how we use cookies and tracking technologies.",
      "url": `${siteUrl}/cookie-policy`
    };

    const existingScript = document.querySelector('script[data-schema="cookie"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'cookie');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="cookie"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gray-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/" className="inline-flex items-center text-gray-300 hover:text-white mb-4 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </a>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
            Cookie Policy
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Cookies allow websites to recognize your device and remember information about your visit, such as your preferred language and other settings. This can make your next visit easier and the site more useful to you.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Curtis Waters AI uses cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Ensure our website functions properly</li>
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve your user experience</li>
              <li>Analyze website traffic and performance</li>
              <li>Provide personalized content and features</li>
              <li>Measure the effectiveness of our marketing campaigns</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Essential Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you, such as setting your privacy preferences, logging in, or filling in forms.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-gray-700 mb-2"><strong>Examples:</strong></p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Session management cookies</li>
                <li>Security cookies</li>
                <li>Load balancing cookies</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Analytics Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use Google Analytics to track website usage and performance.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-gray-700 mb-2"><strong>Google Analytics Cookies:</strong></p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li><code className="bg-gray-200 px-2 py-1 rounded text-sm">_ga</code> - Distinguishes unique users (expires after 2 years)</li>
                <li><code className="bg-gray-200 px-2 py-1 rounded text-sm">_ga_*</code> - Maintains session state (expires after 2 years)</li>
                <li><code className="bg-gray-200 px-2 py-1 rounded text-sm">_gid</code> - Distinguishes users (expires after 24 hours)</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Functional Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies enable enhanced functionality and personalization, such as remembering your preferences, language selection, and region.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-gray-700 mb-2"><strong>Examples:</strong></p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Language preference cookies</li>
                <li>User interface customization cookies</li>
                <li>Video player cookies</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.4 Marketing Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant advertisements on other sites.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Note:</strong> We currently do not use marketing cookies, but this may change in the future. We will update this policy accordingly.</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In addition to our own cookies, we may use various third-party cookies to report usage statistics and deliver advertisements.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 Google Analytics</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use Google Analytics to analyze website traffic and user behavior. Google Analytics uses cookies to collect information about how visitors use our site. This information is used to compile reports and help us improve our website.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              The information collected by Google Analytics includes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Number of visitors to the site</li>
              <li>Pages visited and time spent on each page</li>
              <li>Geographic location (country/city level)</li>
              <li>Browser and device information</li>
              <li>Referring websites</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              For more information about Google Analytics cookies, visit: <a href="https://developers.google.com/analytics/devguides/collection/analyticsjs/cookie-usage" target="_blank" rel="noopener noreferrer nofollow" className="text-red-600 hover:text-red-700 underline cursor-pointer">Google Analytics Cookie Usage</a>
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cookie Duration</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies can be either "session" cookies or "persistent" cookies:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.1 Session Cookies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Session cookies are temporary and are deleted when you close your browser. They help us track your movement from page to page so you don't get asked for information you've already provided.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 Persistent Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              Persistent cookies remain on your device for a set period or until you delete them. They help us recognize you as a returning visitor and remember your preferences.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. How to Control Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.1 Browser Settings</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Most web browsers allow you to control cookies through their settings. You can set your browser to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Block all cookies</li>
              <li>Block third-party cookies only</li>
              <li>Delete cookies when you close your browser</li>
              <li>Accept cookies from specific websites</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Here are links to cookie management instructions for popular browsers:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer nofollow" className="text-red-600 hover:text-red-700 underline cursor-pointer">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer nofollow" className="text-red-600 hover:text-red-700 underline cursor-pointer">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer nofollow" className="text-red-600 hover:text-red-700 underline cursor-pointer">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer nofollow" className="text-red-600 hover:text-red-700 underline cursor-pointer">Microsoft Edge</a></li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.2 Opt-Out of Google Analytics</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can opt-out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on:
            </p>
            <p className="text-gray-700 leading-relaxed">
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer nofollow" className="text-red-600 hover:text-red-700 underline cursor-pointer">Google Analytics Opt-out Browser Add-on</a>
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.3 Important Note</h3>
            <p className="text-gray-700 leading-relaxed">
              Please note that if you block or delete cookies, some features of our website may not function properly, and your user experience may be affected.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Do Not Track Signals</h2>
            <p className="text-gray-700 leading-relaxed">
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want to have your online activity tracked. Currently, there is no industry standard for how to respond to DNT signals. At this time, our website does not respond to DNT signals, but we are committed to respecting your privacy preferences.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Updates to This Cookie Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. More Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For more information about how we handle your personal data, please see our <a href="/privacy-policy" className="text-red-600 hover:text-red-700 underline cursor-pointer">Privacy Policy</a>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about our use of cookies, please contact us at <a href="mailto:curtis@curtiswaters.com" className="text-red-600 hover:text-red-700 underline cursor-pointer">curtis@curtiswaters.com</a>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Cookie Policy, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Curtis Waters AI</strong></p>
              <p className="text-gray-700 mb-2">Email: <a href="mailto:curtis@curtiswaters.com" className="text-red-600 hover:text-red-700 underline cursor-pointer">curtis@curtiswaters.com</a></p>
              <p className="text-gray-700">Website: <a href="https://curtiswaters.ai" className="text-red-600 hover:text-red-700 underline cursor-pointer">curtiswaters.ai</a></p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}